<template>
  <header ref="tabs" class="generic-toolbar">
    <nav :class="{ '-sticky': sticky, '-stickyHeader': stickyHeader }">
      <div class="container">
        <ul
          ref="scroller"
          class="hide-scrollbar"
          @scroll="onUlScroll"
          @mousedown="dragStart"
          @mousemove="dragMove"
          @mouseup="dragEnd"
          @mouseleave="dragEnd"
        >
          <li v-for="(link, i) in items" :key="link.text" :class="{'preventClick': dragMoved}">
            <button v-if="category" type="button" class="h5" :class="{'-active': link.category === selectedCategory}" @click.prevent="handleCategorySelection(i, link.repairJourneyGtm)" draggable="false">
              {{ link.text }}
            </button>
            <router-link v-else-if="link.url && !link.url.includes('http')" :to="localizedUrl + link.url" class="h5" :class="{'-active': $route.path === localizedUrl + link.url}" @click.native="handleCategorySelection(i, link.repairJourneyGtm)" draggable="false">
              {{ link.text }}
            </router-link>
            <a v-else-if="link.url && link.url.includes('http')" :href="link.url" class="h5" :class="{'-active': $route.path === localizedUrl + link.url}" @click="handleCategorySelection(i, link.repairJourneyGtm)" draggable="false">
              {{ link.text }}
            </a>
          </li>
        </ul>
        <div class="fade fade-left" v-show="canScrollLeft"></div>
        <div class="fade fade-right" v-show="canScrollRight"></div>
      </div>
    </nav>
  </header>
</template>

<script>
import { ReadyHook } from 'vue-asyncready'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { repairJourneyEvent } from '../utilities'

export default {
  name: 'GenericToolbar',
  props: ['item', 'items', 'siblingsComponents', 'pagePosition', 'category'],
  mixins: [ReadyHook],
  data() {
    return {
      sticky: false,
      stickyHeader: false,
      selectedCategory: 0,
      canScrollLeft: false,
      canScrollRight: false,
      isDragging: false,
      dragStartX: 0,
      dragScrollLeft: 0,
      dragMoved: false
    }
  },
  computed: {
    localizedUrl () {
      const { url } = currentStoreView()
      return url
    }
  },
  mounted() {
    window.addEventListener('scroll', this.headerPosition)
    window.addEventListener('resize', this.updateFadeVisibility)
    this.updateFadeVisibility()
    document.documentElement.style.setProperty('--generic-toolbar-h', `${this.$refs.tabs.offsetHeight}px`)
  },
  destroyed() {
    window.removeEventListener('scroll', this.headerPosition)
    window.removeEventListener('resize', this.updateFadeVisibility)
  },
  methods: {
    handleCategorySelection(category, gtmStepName) {
      this.selectCategory(category)
      if (gtmStepName) {
        repairJourneyEvent({stepName: gtmStepName, stepNumber: 1})
      }
    },
    headerPosition() {
      if (window) {
        let elemRect = this.$refs.tabs.getBoundingClientRect()

        const getShouldBeSticky = () => {
          if (this.item.stickyOnScroll) {
            return window.pageYOffset > elemRect.top + document.documentElement.scrollTop
          }

          return window.pageYOffset + elemRect.top + document.documentElement.scrollTop > elemRect.top
        }

        if (getShouldBeSticky()) {
          this.sticky = document.querySelector('#header-site').classList.contains('-fixed')
          this.stickyHeader = document.querySelector('#header-site').classList.contains('-show')
        } else {
          this.sticky = false
          this.stickyHeader = false
        }
      }
    },
    selectCategory (category) {
      this.selectedCategory = category
      this.$emit('selectedCategory', category)
      this.scrollTo()
    },
    scrollTo() {
      if (window) {
        let bodyRect = document.body.getBoundingClientRect()
        let elemRect = this.$refs.tabs.getBoundingClientRect()
        let offset = elemRect.top - bodyRect.top
        // let decal = window.scrollY > offset ? 120 : 80
        window.scrollTo({
          top: offset - 120,
          behavior: 'smooth'
        })
      }
    },
    onUlScroll() {
      this.updateFadeVisibility()
    },
    updateFadeVisibility() {
      const el = this.$refs.scroller
      if (!el) {
        this.canScrollLeft = false
        this.canScrollRight = false
        return
      }
      const scrollLeft = Math.round(el.scrollLeft)
      const maxScrollLeft = Math.round(el.scrollWidth - el.clientWidth)

      this.canScrollLeft = scrollLeft > 0
      this.canScrollRight = scrollLeft < (maxScrollLeft - 1)
    },
    dragStart(e) {
      const el = this.$refs.scroller
      this.isDragging = true
      this.dragStartX = e.pageX - el.offsetLeft
      this.dragScrollLeft = el.scrollLeft
      this.dragMoved = false
    },
    dragMove(e) {
      if (!this.isDragging) return
      e.preventDefault()
      const el = this.$refs.scroller
      const x = e.pageX - el.offsetLeft
      const delta = x - this.dragStartX
      if (Math.abs(delta) > 0) this.dragMoved = true
      el.scrollLeft = this.dragScrollLeft - (x - this.dragStartX)
      this.updateFadeVisibility()
    },
    dragEnd() {
      this.isDragging = false
      this.dragMoved = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

header {
  position: relative;
  height: 8.1rem;
  @include bp(max md) {
    height: 5.1rem;
  }
}

nav {
  position: absolute;
  z-index: 30;
  top: 0;
  width: 100%;
  background: var(--zenith-white);

  .container {
    position: relative;

    @include bp(max md) {
      padding: 0;
    }

    .fade{
      position: absolute;
      top: 0;
      height: 100%;
      background: linear-gradient(to right, transparent 0%, var(--zenith-app-background) 100%);
      width: 12rem;
      margin: 0 4rem;
      pointer-events: none;

      &-left{
        right: auto;
        left: 0;
        transform: rotate(180deg);
      }

      &-right{
        right: 0;
        left: auto;
      }

      @include bp(max md) {
        margin: 0;
      }
    }
  }

  &.-sticky {
    position: fixed;
    transition: all 0.2s ease-in-out;
    transform: translateY(-8rem);

    ul:before {
      display: none;
    }

    &.-stickyHeader {
      transform: translateY(12rem);
      @include bp(max md) {
        transform: translateY(9rem);
      }
    }
  }

  ul {
    position: relative;
    margin: 0;
    padding: 0;
    list-style: none;
    overflow-x: auto;
    white-space: nowrap;
    text-align: center;
    display: flex;
    justify-content: space-evenly;
    user-select: none;

    @include bp(max md) {
      padding: 0 1.5rem;
    }

    li {
      padding: 0 3rem;
      display: inline-block;

      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        padding-right: 0;
      }

      &.preventClick {
        pointer-events: none;
      }

      a, button {
        display: block;
        padding: 3.4rem 0 0;
        height: 8rem;
        border: none;
        background: none;
        color: var(--zenith-theme-primary-lighter);
        text-transform: uppercase;
        transition: all 0.2s ease-in-out;
        border-bottom: 2px solid transparent;
        outline: none;

        @include bp(max md) {
          padding-top: 1.8rem;
          height: 5rem;
        }

        &:hover, &.-active {
          border-bottom: 2px solid var(--zenith-theme-primary);
        }
      }
    }
  }
}
</style>
